"use client";

import React from "react";
import {PageCompetitionBookTySchema} from "@/sanity/pages/PageCompetitionBookTyTemplate";

import Image from "next/image";
import DividerGreenLine from "@/components/global/ui/DividerGreenLine";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";

import prize1 from "@/public/competition-book-ty/prize-1.png";
import prize2 from "@/public/competition-book-ty/prize-2.png";
import prize3 from "@/public/competition-book-ty/prize-3.png";

const prizeItems = [
  {
    border: "border-t-[2px] border-t-[#F7AD07]",
    bg: "bg-gradient-to-b from-[#F7AD07] opacity-[.16]",
    place: "1st",
    placeClasses: "bg-[#F7AD07] text-[#09171A]",
    image: prize1,
    imageClasses: "w-[min(40.75rem,98%)] [filter:drop-shadow(8px_8px_2px_rgba(0,0,0,.12))_drop-shadow(0_12px_52px_rgba(0,0,0,.3))]"
  },
  {
    border: "border-t-[2px] border-t-[#D8D8D8]",
    bg: "bg-gradient-to-b from-[#D8D8D8] opacity-[.16]",
    place: "2nd",
    placeClasses: "bg-[#D8D8D8] text-[#09171A]",
    image: prize2,
    imageClasses: "w-[min(42.5rem,98%)] [filter:drop-shadow(8px_8px_2px_rgba(0,0,0,.12))_drop-shadow(0_12px_52px_rgba(0,0,0,.3))]"
  },
  {
    border: "border-t-[2px] border-t-[#BB701A]",
    bg: "bg-gradient-to-b from-[#BB701A] opacity-[.16]",
    place: "3rd",
    placeClasses: "bg-[#BB701A] text-white",
    image: prize3,
    imageClasses: "w-full"
  }
]

export default function PrizesSection({
  data,
  className
}: {
  data: PageCompetitionBookTySchema;
  className?: string;
}) {
  return (
    <section className={cn(
      "relative overflow-clip pb-[8rem] lg:pb-[15rem]",
      className
    )}>
      <div className="container flex flex-col items-center">
        {data?.prizesText && (
          <div className="richtext prose-h2:text-white prose-h2:font-ivy prose-h2:italic prose-h2:pb-[4rem] lg:prose-h2:pb-[5rem]">
            <PortableText
              value={data.prizesText}
              components={richTextComponent}
            />
          </div>
        )}

        {data?.prizesItems && (
          <div className="w-[min(145.75rem,100%)] flex flex-col gap-[8rem] lg:gap-[15rem]">
            <div className="flex flex-col items-center lg:items-stretch lg:grid lg:grid-cols-3 gap-[3rem]">
              {data?.prizesItems.map((step, index) => (
                <div
                  key={index}
                  className={cn(
                    "bg-[#112628] flex flex-col items-center p-[3.5rem] sm:p-[4rem] relative aspect-[398/464] lg:aspect-[372/464] w-full sm:w-[50rem] lg:w-auto",
                    prizeItems[index % prizeItems.length].border
                  )}
                >
                  <div className={cn(
                    "absolute pointer-events-none top-0 left-0 w-full h-full",
                    prizeItems[index % prizeItems.length].bg
                  )}/>

                  <p className={cn(
                    "flex items-center justify-center min-w-[6.125rem] min-h-[3.25rem] rounded-[.25rem] font-geistmono font-semibold text-[2.25rem] leading-none mb-[4rem]",
                    prizeItems[index % prizeItems.length].placeClasses
                  )}>
                    {prizeItems[index % prizeItems.length].place}
                  </p>

                  {step?.text && (
                    <div className="richtext prose-p:text-white prose-p:text-[3.75rem] prose-p:leading-[1.1em]">
                      <PortableText
                        value={step.text}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  <Image
                    className={cn(
                      "absolute bottom-0 pointer-events-none",
                      prizeItems[index % prizeItems.length].imageClasses
                    )}
                    src={prizeItems[index % prizeItems.length].image}
                    alt=""
                  />
                </div>
              ))}
            </div>

            <DividerGreenLine
              className="w-full"
            />
          </div>
        )}
      </div>
    </section>
  );
}
